/* eslint-disable prefer-destructuring */
/* eslint-disable react-hooks/exhaustive-deps */
import AppRoutes from './routes/AppRoutes';
import { ThemeProvider } from '@mui/material/styles';
import { theme } from './utils/theme';
import './App.css';
import { Auth0Provider } from '@auth0/auth0-react';
import axios from 'axios';
import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from './redux/hooks';
import { fetchOrgId } from './redux/slices/orgId/orgIdSlice';
import { ToastContainer, toast } from 'react-toastify';
import { EDITING_FROZEN_MESSAGE } from './utils/constants';
import 'react-toastify/dist/ReactToastify.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
// import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { fetchAcsToken } from './redux/slices/acs-token/acsTokenSlice';
import { getUserDetails } from './utils/orgName';
import { getProgramContext } from './hooks/useProgramContext';
import _ from 'lodash';
import { persistor, store } from './redux/store';
import DialogSelector from './components/DialogUi/DialogSelector';
import { checkSession } from './utils/checkSeesion';
import { isLoggingOut } from './utils/logoutInProgress';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';
import { captureOpenProgramIntent } from './utils/openProgramIntent';

// Co-design invite deep links (email / Teams) land here with ?openProgram=;
// stash it before login + RoleRedirect drop the query string.
captureOpenProgramIntent();

/**
 * React Query's own defaults treat every response as stale the moment it
 * arrives, so returning to a page refetched everything and showed its loading
 * state again, as did alt-tabbing back into the browser. Only 13 of the 37
 * query hooks set their own staleTime, so the rest inherited that behaviour.
 *
 * Thirty seconds is deliberately short: it removes the repeated fetches within
 * a single stretch of navigation without letting anyone sit on stale data.
 * Hooks that need fresher or longer-lived data still override these.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,
      refetchOnWindowFocus: false
    }
  }
});
const role = localStorage.getItem('role');

axios.interceptors.request.use((request: any) => {
  // On the way out to Auth0 nothing more goes to the server: the tokens are already cleared, which
  // reads as an expired session below, and reloading then would cancel the logout
  // (utils/logoutInProgress.ts). The request is simply never sent; the page is leaving.
  if (isLoggingOut()) return new Promise(() => {});
  const tokens = localStorage.getItem('tokens') ? JSON.parse(localStorage.getItem('tokens') || '') : {};
  if (!request.url.includes('/organization/') && checkSession()) {
    localStorage.clear();
    window.location.replace('/');
  }

  const { location: resolvedLocation } = getProgramContext();

  const req = {
    ...request,
    headers: {
      ...request.headers,
      authorization: `Bearer ${tokens.access_token}`,
      idtoken: tokens.id_token,
      // Respect an explicitly-provided location header (e.g. creating an org in
      // a different region than the admin's own — addOrganization sends the
      // target region). Only fall back to the context-derived location when the
      // caller didn't set one, otherwise the BE rejects with "Location Mismatch".
      location: request.headers?.location || resolvedLocation
    }
  };
  return req;
});

// Editing freeze (B4-2): frozen write endpoints answer 423 { frozen: true }.
// One toast with the server message, deduped so a burst of requests (debounced
// saves, cascades) does not stack identical toasts.
let lastFrozenToastAt = 0;
const FROZEN_TOAST_DEDUPE_MS = 5000;

axios.interceptors.response.use(
  (response: any) => {
    return response;
  },
  async (error) => {
    if (_.get(error, 'response.status') === 423 && _.get(error, 'response.data.frozen') === true) {
      const now = Date.now();
      if (now - lastFrozenToastAt > FROZEN_TOAST_DEDUPE_MS) {
        lastFrozenToastAt = now;
        toast.warn(_.get(error, 'response.data.message') || EDITING_FROZEN_MESSAGE);
      }
      return Promise.reject(error);
    }
    const messages = ['Session has expired', 'Unauthorized', 'Unauthorized Access'];
    if (_.get(error, 'response.data.code') === 401 && messages.includes(_.get(error, 'response.data.message'))) {
      // [INSTRUMENTATION feat/program-design-blank-screen] surface silent-401 swallow
      try {
        const url = _.get(error, 'response.config.url') || _.get(error, 'response.request.responseURL') || 'unknown';
        const method = _.get(error, 'response.config.method', 'unknown');
        const body = _.get(error, 'response.data');
        console.warn('[auth-401-swallow]', { url, method, body, time: new Date().toISOString() });
        const buf = JSON.parse(localStorage.getItem('__dosen_debug_401') || '[]');
        buf.push({ url, method, body, time: new Date().toISOString() });
        localStorage.setItem('__dosen_debug_401', JSON.stringify(buf.slice(-20)));
      } catch {
        /* never let debug code break the app */
      }
      localStorage.removeItem('persist:root');
      persistor.purge();
      localStorage.removeItem('lastseen');
      // TODO: need to handle this separately
      // window.location.replace('/');
      return;
    }
    const state = store.getState();
    const acsTokenLoading = state.acsToken.loading;

    if (!acsTokenLoading) {
      // Requests that fail without an HTTP response (timeout, network drop,
      // aborted) have no error.response — fall back to '' so .includes()
      // doesn't throw and mask the original error.
      const failedUrl = _.get(error, 'response.request.responseURL') || '';
      if (failedUrl.includes('/api/azureToken') && _.get(error, 'response.status') === 401) {
        const { communicationUserId } = getUserDetails();
        if (communicationUserId) {
          store.dispatch(fetchAcsToken({ communicationId: communicationUserId, tag: '#self' }));
        }
      } else if (failedUrl.includes('/api/getChatThread') && _.get(error, 'response.status') === 401) {
        const { communicationUserId } = getUserDetails();
        if (communicationUserId) {
          store.dispatch(fetchAcsToken({ communicationId: communicationUserId, tag: '#self' }));
        }
      }
    }

    throw error;
  }
);

function App() {
  const urlParams = new URLSearchParams(window.location.search);
  const invitation = urlParams.get('invitation');
  const orgName = urlParams.get('organization_name') || '';
  const organization = urlParams.get('organization');
  let domain = urlParams.get('org') || '';
  const hashURL = window.location.hash;

  /**
   * this if condition is to logout the previous user in case of new invitation
   */
  if (localStorage.getItem('tokens') && invitation) {
    localStorage.clear();
    window.location.reload();
  }

  if (hashURL.includes('org=')) {
    const hashIndex: number = hashURL.indexOf('#');
    const hashPart: string = hashIndex !== -1 ? hashURL.slice(hashIndex + 1) : '';
    const orgRegex = /org=([^&]*)/;
    const match = hashPart.match(orgRegex);
    domain = match ? match[1] : '';
  }
  let userType = '';
  const orgDetails = useAppSelector((state) => state.orgIdReducer);
  const dispatch = useAppDispatch();

  const isInvited = invitation && orgName && organization;

  if (!domain && !isInvited) {
    userType = 'platAdmin';
  }

  if (invitation) {
    localStorage.setItem('invitation', JSON.stringify(invitation));
    localStorage.setItem('orgName', orgName);
  }

  const providerConfig = {
    domain: process.env.REACT_APP_AUTH0_DOMAIN || '',
    redirectUri: window.location.origin,
    clientId: process.env.REACT_APP_AUTH0_CLIENT_ID || '',
    audience: process.env.REACT_APP_AUTH0_AUDIENCE || ''
  };

  const config2 = {
    ...providerConfig,
    organization: orgDetails.data?.authOrgId || ''
  };

  const inviteConfig = {
    ...providerConfig,
    invitation,
    organization
  };

  const render = (config: any) => {
    return (
      <ThemeProvider theme={theme}>
        <Auth0Provider cacheLocation="localstorage" useCookiesForTransactions={true} {...config}>
          <QueryClientProvider client={queryClient}>
            <DndProvider backend={HTML5Backend}>
              <AppRoutes />
              <DialogSelector />
            </DndProvider>
            {/* <ReactQueryDevtools /> */}
          </QueryClientProvider>
        </Auth0Provider>
      </ThemeProvider>
    );
  };

  useEffect(() => {
    if (userType === '' && !isInvited) {
      localStorage.setItem('orgName', domain);
      dispatch(fetchOrgId(domain));
    }
  }, [orgName]);

  const checkRole = () => {
    if (userType === 'platAdmin') {
      return render(providerConfig);
    } else {
      if (orgDetails.error) {
        return <div>{orgDetails.errorText}</div>;
      }
      if (orgDetails.loading) {
        return null;
      }
      if (orgDetails.data) {
        return render(config2);
      }
    }
  };

  return (
    <>
      {isInvited ? render(inviteConfig) : checkRole()}
      <ToastContainer />
    </>
  );
}

export default App;
